import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { formatDate } from '@avanta/shared/utils/date';
import { type ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchNextAppointments } from 'hooks/useFetchNextAppointments';
import { useUserStore } from 'stores/user';
import { type Appointment } from 'types/appointments';
import { CONTACT_METHOD_KEY, getCenterName, isUpcomingAppointment } from 'utils/appointments';
import { TableActions } from './TableActions';

export const AppointmentsTable = (): React.ReactElement | null => {
  const patient = useUserStore(state => state.user);
  const { data: nextAppointments } = useFetchNextAppointments(patient?.id ?? '');
  const appointments = nextAppointments?.filter(isUpcomingAppointment);
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);

  const columns: ColumnDef<Appointment>[] = useMemo(
    () => [
      {
        id: 'date',
        accessorKey: 'dateTime',
        header: t('AppointmentsTable.date.label'),
        accessorFn: row => formatDate(row.dateTime, 'dd/MM/yyyy HH:mm'),
        cell: ({ row }) => formatDate(row.original.dateTime, 'dd/MM/yyyy HH:mm'),
        enableSorting: true,
      },
      {
        id: 'psychologist',
        header: t('AppointmentsTable.psychologist.label'),
        cell: ({ row }) =>
          `${row.original.psychologist.psychologist.name} ${row.original.psychologist.psychologist.surname}`,
        accessorFn: row =>
          `${row.psychologist.psychologist.name} ${row.psychologist.psychologist.surname}`,
      },
      {
        id: 'type',
        accessorKey: 'bookedContactMethod',
        header: t('AppointmentsTable.type.label'),
        accessorFn: row =>
          t(
            `AppointmentCard.types.${CONTACT_METHOD_KEY[row.bookedContactMethod] ?? row.bookedContactMethod}`
          ),
        cell: ({ row }) =>
          t(
            `AppointmentCard.types.${CONTACT_METHOD_KEY[row.original.bookedContactMethod] ?? row.original.bookedContactMethod}`
          ),
      },
      {
        id: 'center',
        header: t('AppointmentsTable.center.label'),
        accessorFn: row => getCenterName(row.bookedContactMethod, row.center),
        cell: ({ row }) => getCenterName(row.original.bookedContactMethod, row.original.center),
      },
      {
        id: 'actions',
        header: t('AppointmentsTable.actions.label'),
        cell: ({ row }) => <TableActions appointment={row.original} />,
      },
    ],
    [t]
  );

  if (!appointments?.length) {
    return null;
  }

  return (
    <ClientPaginatedTable
      data={appointments}
      columns={columns}
      withSearch
      title={t('AppointmentsTable.heading')}
    />
  );
};
